import { useSelector } from 'react-redux'
import { useLocation } from 'react-router'

// Components
import SEO from 'components/base/seo'

// Hooks
import { useCurrentQuery } from 'modules/browse/hooks'

// Selectors
import { selectPurgedSearchString } from 'modules/browse/selectors'

// Types
import { State, EntityState, TrackType, Label, Region, Genre } from 'types'

// Utils
import { KEYS_CHOICES, CATEGORIES } from '@constants'

import styles from './searchdescription.css'

const renderSortMode = (sortMode?: number | string) => {
  switch (+(sortMode || 0)) {
    case 2:
      return 'Newly added'
    case 3:
      return 'Alphabetically sorted'
    case 4:
      return 'Alphabetically sorted'
    case 5:
      return 'Freshly released'
    default:
      return 'Popular'
  }
}

const pluralize = (names: Array<string>, lowercase = true): string => {
  const parsedNames = lowercase ? names.map(s => s?.toLowerCase()) : names
  if (parsedNames.length) {
    const lastName = parsedNames.pop()
    if (parsedNames.length) {
      return `${parsedNames.join(', ')} and ${lastName}`
    }
    return lastName || ''
  }
  return ''
}

const renderTypes = (selectedTypes: Array<number> | null | undefined, trackTypeOptions: EntityState<TrackType>) => {
  return selectedTypes && Object.keys(trackTypeOptions).length >= selectedTypes.length
    ? pluralize(selectedTypes.map(itemId => trackTypeOptions[String(itemId)].name))
    : ''
}

const renderRegions = (selectedRegions: Array<number> | null | undefined, regionOptions: EntityState<Region>) =>
  selectedRegions && Object.keys(regionOptions).length
    ? pluralize(
        selectedRegions.map(itemId => regionOptions[String(itemId)].name),
        false,
      )
    : ''

const renderLabels = (selectedLabels: Array<number> | null | undefined, labelOptions: EntityState<Label>) => {
  return selectedLabels && Object.keys(labelOptions).length
    ? pluralize(
        selectedLabels.map(itemId => labelOptions[String(itemId)].name),
        false,
      )
    : ''
}

const renderGenres = (selectedGenres: Array<string> | null | undefined, genreOptions: EntityState<Genre>) =>
  Object.keys(genreOptions).length && selectedGenres
    ? pluralize(selectedGenres.map(itemId => Object.values(genreOptions).find(genre => genre.slug === itemId)?.name as string))
    : ''

const renderKeys = (selectedKeys: Array<string> | null | undefined) =>
  selectedKeys
    ? pluralize(
        selectedKeys.map(itemId => {
          const key = KEYS_CHOICES.find(x => x.id === itemId)
          if (key) {
            return key.name
          }
          return ''
        }),
        false,
      )
    : ''

const renderCategories = (selectedCategories: Array<number> | undefined): string => {
  if (!selectedCategories) {
    return ''
  }
  const categoryMap = selectedCategories.map(itemId => {
    const category = CATEGORIES[itemId]
    if (category) {
      const split = category.split(' ')
      return split.pop() as string
    }
    return ''
  })
  return pluralize(categoryMap, false)
}

const renderYear = (minReleaseYear: number | null | undefined, maxReleaseYear: number | null | undefined) => {
  if (minReleaseYear && maxReleaseYear && minReleaseYear === maxReleaseYear) {
    return `${maxReleaseYear} `
  } else if (!minReleaseYear && !maxReleaseYear) {
    return ''
  } else if (!minReleaseYear && maxReleaseYear) {
    return `${maxReleaseYear} and earlier `
  } else if (minReleaseYear && !maxReleaseYear) {
    return `${minReleaseYear} and later `
  } else if (minReleaseYear && maxReleaseYear) {
    return `${minReleaseYear} to ${maxReleaseYear} `
  }
  return ''
}

const renderBPM = (minBPM: number | null | undefined, maxBPM: number | null | undefined) => {
  if (minBPM && maxBPM && minBPM === maxBPM) {
    return `in ${maxBPM} BPM `
  } else if (!minBPM && !maxBPM) {
    return ''
  } else if (!minBPM && maxBPM) {
    return `in ${maxBPM} BPM and slower `
  } else if (minBPM && !maxBPM) {
    return `in ${minBPM} BPM and faster `
  } else if (minBPM && maxBPM) {
    return `between ${minBPM} BPM and ${maxBPM} BPM `
  }
  return ''
}

interface SearchDescriptionProps {
  asString?: boolean
}

const SearchDescription = ({ asString }: SearchDescriptionProps): JSX.Element | null => {
  const { search: unpurgedSearch } = useLocation()
  const search = selectPurgedSearchString(unpurgedSearch)
  const count = useSelector<State, number>(s => s.browse.results[search]?.count)
  const adminModeActive = useSelector<State, boolean>(s => s.auth.adminModeActive)
  const currentQuery = useCurrentQuery()
  const genreOptions = useSelector<State, EntityState<Genre>>(s => s.entities.genres)
  const regionOptions = useSelector<State, EntityState<Region>>(s => s.entities.regions)
  const trackTypeOptions = useSelector<State, EntityState<TrackType>>(s => s.entities.trackTypes)
  const labelOptions = useSelector<State, EntityState<Label>>(s => s.entities.labels)

  if (!currentQuery) {
    return null
  }
  const {
    bpm_over: minBPM,
    bpm_under: maxBPM,
    categories: selectedCategories,
    genres: selectedGenres,
    keys: selectedKeys,
    labels: selectedLabels,
    ordering: sortMode,
    owned,
    q: texts,
    regions: selectedRegions,
    release_year_after: minReleaseYear,
    release_year_before: maxReleaseYear,
    types: selectedTypes,
    multitrack,
  } = currentQuery
  const hasTypes = selectedTypes && selectedTypes.length > 0
  const hasPhrases = !!texts
  const hasRegions = selectedRegions && selectedRegions.length > 0
  const hasGenres = selectedGenres && selectedGenres.length > 0
  const hasLabels = selectedLabels && selectedLabels.length > 0
  const hasKeys = selectedKeys && selectedKeys.length > 0
  const hasCategories = selectedCategories && selectedCategories.length > 0
  const hasYear = maxReleaseYear || minReleaseYear
  const hasBPM = maxBPM || minBPM
  const hasMultitrack = !!multitrack
  const description = `${adminModeActive && count ? `${count} ` : ''}${renderSortMode(sortMode)} ${
    hasGenres ? `${renderGenres(selectedGenres, genreOptions)} ` : ''
  }${hasTypes ? `${renderTypes(selectedTypes, trackTypeOptions)} ` : ''}${hasMultitrack ? 'multi-' : ''}tracks to sample ${
    hasPhrases ? 'containing ' : ''
  }${hasPhrases ? `${texts || ''} ` : ''}${hasYear ? 'from ' : ''}${hasYear ? renderYear(minReleaseYear, maxReleaseYear) : ''}${
    hasBPM ? renderBPM(minBPM, maxBPM) : ''
  }${hasRegions ? 'from ' : ''}${hasRegions ? renderRegions(selectedRegions, regionOptions) : ''}${hasLabels ? ' on ' : ''}${
    hasLabels ? renderLabels(selectedLabels, labelOptions) : ''
  }${hasKeys ? ' in ' : ''}${hasKeys ? renderKeys(selectedKeys) : ''}${hasCategories ? ' with license category ' : ''}${
    hasCategories ? renderCategories(selectedCategories) : ''
  }${owned ? ' that you own' : ''}`.replace(/\s+/g, ' ')
  return (
    <>
      <SEO title={`${description} | Tracklib`} />
      {asString ? (
        description
      ) : (
        <h1 id="barTitle" title={description} className={styles.title} datatesting="browse_filter_header">
          {description}
        </h1>
      )}
    </>
  )
}

export default SearchDescription
